<template>
  <aside ref="vacacncy-nav" class="vacancy-navigation" :style="{ minHeight: vacancyNavHeight + 'px' }">
    <div ref="sticky" class="sticky" :class="{ 'is-sticky': isSticky }">
      <navigation :is-hidden-country="isHiddenCountry" />

      <vcv-vacancy-social-widget
        class="vacancy-navigation__widget"
        :title="$tr('common.list.landing')"
        :caption="$tr('vacancy.basic.social_widget_caption')"
        :link="customLink"
      />

      <div class="vacancy-navigation__widget">
        <div class="nav-widget_header">
          <div class="nav-widget_title">{{ $tr('actions.invite') }}</div>
        </div>

        <p class="nav-widget_description">
          {{ $tr('vacancy.navigation.invite.text') }}
        </p>

        <vbutton :text="$tr('actions.invite')" color="grey" text_color="grey-dark" @click="invite" />
      </div>

      <div v-if="response_number" class="vacancy-navigation__widget">
        <div class="nav-widget_header">
          <div class="nav-widget_title">{{ $tr('responses.title') }}</div>
          <div class="nav-widget_note">{{ response_number }}</div>
        </div>

        <widget-responses :vacancy="vacancy" :count="response_number" />
      </div>
    </div>
  </aside>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { check_hidden_country } from '@/_helpers/utils';
import Navigation from '@/routes_new/inner/vacancy/components/navigation/VacancyNavigation';
import WidgetResponses from '@/routes_new/inner/vacancy/components/widget-responses';
import VcvVacancySocialWidget from '@/routes_new/inner/vacancy/components/VcvVacancySocialWidget';

export default {
  name: 'InnerVacancySidebar',

  components: {
    VcvVacancySocialWidget,
    Navigation,
    WidgetResponses
  },

  data() {
    return {
      scrollPos: 0,
      stickyHeight: 0,
      isScrolledTop: false,
      topPos: 40,
      isSticky: false,
      vacancyNavHeight: 600
    };
  },

  computed: {
    ...mapState('vacancy', ['landing_url']),

    ...mapState('responses', ['responses', 'filter']),

    ...mapGetters('vacancy', { vacancy: 'one' }),

    customLink() {
      const domain = `${window.location.origin}/r/`;

      return this.vacancy.landing_url_path === null || this.vacancy.landing_url_path === ''
        ? `${domain}${this.vacancy.token}`
        : `${domain}${this.vacancy.landing_url_path}`;
    },
    // End Social widget

    response_number() {
      return this.filteredResponses && this.filteredResponses.length ? this.filteredResponses.length : 0;
    },

    filteredResponses() {
      if (this.responses === undefined || this.responses.length === 0) {
        return [];
      }

      return this.responses.filter(item => !item.is_hidden);
    },

    isHiddenCountry() {
      if (!this.vacancy || !this.vacancy.country) {
        return true;
      }

      return check_hidden_country(this.vacancy.country.code);
    }
  },

  created() {
    this.applyFilter();
  },

  mounted() {
    window.addEventListener('scroll', this.scrollNav);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.scrollNav);
  },

  destroyed() {
    if (this.$route.name !== 'responses') {
      this.clearFilter();
    }
  },

  methods: {
    ...mapMutations('responses', ['set_filter']),

    ...mapActions('responses', ['updateFilters']),

    // Scroll behavior
    scrollNav() {
      const $el = this.$refs.sticky;
      const elScrollTop = document.scrollingElement || document.documentElement;
      this.stickyHeight = $el.offsetHeight + this.$refs.sticky.getBoundingClientRect().top + elScrollTop.scrollTop;
      this.vacancyNavHeight = $el.offsetHeight + 60;
      let scrollOffset = window.pageYOffset;

      if (document.body.getBoundingClientRect().top > this.scrollPos) {
        this.isSticky = false;

        if (scrollOffset > this.stickyHeight && !this.isScrolledTop) {
          this.topPos = scrollOffset - $el.offsetHeight;
        } else if (scrollOffset < this.stickyHeight - $el.offsetHeight || scrollOffset < 90) {
          this.topPos = scrollOffset;
          this.isSticky = true;
        }
        this.isScrolledTop = true;
      } else {
        this.isScrolledTop = false;
        this.isSticky = false;
      }

      this.scrollPos = document.body.getBoundingClientRect().top;
    },

    invite() {
      this.$router.push({
        name: 'invites-creation',
        query: { id: this.vacancy.id, name: this.vacancy.name }
      });
    },

    applyFilter() {
      let query_filter = [...this.filter.query_filter];

      query_filter.push('vacancy_id');
      this.set_filter({ key: 'query_filter', v: query_filter });
      this.set_filter({ key: 'vacancy_id', v: [this.vacancy.id] });

      this.updateFilters({ data: { vacancy: this.vacancy } });
    },

    clearFilter() {
      let query_filter = this.filter.query_filter;

      this.set_filter({ key: 'query_filter', v: query_filter });
      this.set_filter({ key: 'vacancy_id', v: [] });

      this.updateFilters({ data: { vacancy: this.vacancy } });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.vacancy-navigation {
  display: flex;
  flex-direction: column;

  .nav_link {
    display: flex;
    align-items: center;
    font-style: normal;
    font-weight: normal;
    line-height: 22px;
    font-size: 15px;
    letter-spacing: 0.005em;
    margin-bottom: 20px;
    color: #232323;

    &:hover,
    &.router-link-active {
      color: $green;
    }
  }

  .nav_link_text {
    display: block;
    margin-right: 14px;
  }

  &__disabled {
    color: #9faab6;

    &:hover {
      color: #9faab6;
    }
  }

  &__widget {
    margin: 0 20px 10px;
    background: #ffffff;
    box-shadow: $box-shadow;
    border-radius: 2px;
    padding: 16px;

    .nav-widget_title {
      @include mixin-text-header;
      color: $main-dark;
    }

    .nav-widget_description {
      @include mixin-text-tag;
      color: $grey-darker;
      margin-bottom: 8px;
    }

    .nav-widget_header {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      .nav-widget_note {
        margin-left: 4px;
      }
    }

    .nav-widget_note {
      @include mixin-text-tag;
      color: $grey-darker;
    }
  }
}

.responses-section {
  margin-top: 5px;
  padding-top: 20px;
  border-top: 1px solid #d2d6e0;
  width: 120px;
}

.responses-section_link {
  display: flex;
  align-items: center;
}

.responses-section_icon {
  margin-left: 10px;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticky {
  position: relative;
  margin-bottom: 80px;
  width: 300px;

  &.is-sticky {
    position: fixed;
    top: 90px !important;
  }
}
</style>
